import { useMutation } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { NegotiationCaseCreateFormData } from '../../common/common.schema';
import { TsrctLanguage } from '@/generated/gql/graphql';
import { mapToTsrctNegotiationStrategyBoundaryRuleInput } from '~/components/NegotiationTermsForm/NegotiationTermsForm.util';
import { SupplierContactHitId } from '~/components/SupplierContactAutocomplete';
import { DEMAND_CASES_QUERY } from '~/modules/DemandCasesWidget/DemandCasesQuery.gql';
import { useDemandDetail } from '~/modules/Module.hooks';

import { DEMAND_CASE_ADD_MUTATION } from './DemandCaseAddMutation.gql';
import { DEMAND_CASE_RELEASE_MUTATION } from './DemandCaseReleaseMutation.gql';

export const useLegacyNegotiationCaseCreation = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'DemandAddSupplierDialog' });
  const { enqueueErrorSnackbar } = useCustomSnackbar();

  const { demandId } = useDemandDetail();
  const [createNegotiationMutation, { loading: loadingMutation }] = useMutation(DEMAND_CASE_ADD_MUTATION, {
    refetchQueries: [
      {
        query: DEMAND_CASES_QUERY,
        variables: {
          id: demandId,
        },
      },
    ],
  });
  const [releaseNegotiationMutation] = useMutation(DEMAND_CASE_RELEASE_MUTATION);

  const createNegotiation = async ({ supplier, ...terms }: NegotiationCaseCreateFormData) => {
    try {
      const response = await createNegotiationMutation({
        variables: {
          demandId,
          data: {
            supplier: {
              ...supplier,
              contact: {
                ...supplier.contact,
                language: supplier.contact?.language as TsrctLanguage,
                internalId:
                  supplier.contact?.internalId === SupplierContactHitId.NEW ? null : supplier.contact?.internalId,
              },
            },
            strategy: {
              boundaryAdjustments: terms.boundaryAdjustments,
              boundaryRules: mapToTsrctNegotiationStrategyBoundaryRuleInput(terms.boundaries),
            },
            options: terms.options,
          },
        },
      });

      return response;
    } catch (error) {
      enqueueErrorSnackbar(t('error.caseCreate'));
    }
  };

  const releaseNegotiation = async ({ id }: { id: string }) => {
    try {
      await releaseNegotiationMutation({
        variables: {
          id: demandId,
          data: {
            negotiationCaseIds: [id],
          },
        },
      });
    } catch (error) {
      enqueueErrorSnackbar(t('error.caseRelease'));
    }
  };

  return {
    loading: loadingMutation,
    createNegotiation,
    releaseNegotiation,
  };
};
